/**
 * 834CH の放送状態を持つストア（要件定義 3.2 / 3.5）。
 *
 * - サーバー時刻との差を保持し、放送位置は常にサーバー時刻基準で求める
 * - 1 秒ごとに放送時刻を進める
 * - タブ復帰・オンライン復帰で放送情報を取り直す
 */

import type { NowResponse } from '@shared/api.ts'
import { createEffect, createResource, createSignal, onCleanup, onMount } from 'solid-js'
import { fetchNow } from '~/lib/api'
import { computeClockOffset, programOffsetSeconds } from '~/lib/clock'

const TICK_MS = 1000

/** 番組切り替え時の再取得が連打にならないよう空ける間隔 */
const ADVANCE_COOLDOWN_MS = 3000

/** 放送休止中に復帰を確認しにいく間隔 */
const OFF_AIR_RETRY_MS = 60_000

/** 再生できなかった動画を覚えておく上限。クエリ文字列が伸びすぎないように */
const MAX_UNAVAILABLE = 20

export type BroadcastStore = ReturnType<typeof createBroadcastStore>

export const createBroadcastStore = () => {
  const [clockOffsetMs, setClockOffsetMs] = createSignal(0)
  const [tickMs, setTickMs] = createSignal(Date.now())
  /** 再生できなかった videoId。次の放送可能な番組へ送るために API に渡す */
  const [unavailable, setUnavailable] = createSignal<string[]>([])

  const [now, { refetch }] = createResource(unavailable, async (excluded) => {
    const requestedAt = Date.now()
    const response = await fetchNow(excluded)
    // 往復時間の半分を足して、サーバー時刻をおおまかに補正する
    const roundTrip = Date.now() - requestedAt
    setClockOffsetMs(computeClockOffset(response.serverTime, requestedAt + roundTrip / 2))
    setTickMs(Date.now())
    return response
  })

  /** サーバー時刻基準の現在時刻（ミリ秒） */
  const broadcastNowMs = (): number => tickMs() + clockOffsetMs()

  /** 現在番組の再生開始位置（秒） */
  const broadcastOffsetSeconds = (): number => {
    const program = now()?.now
    if (!program) return 0
    return programOffsetSeconds(program.startAt, broadcastNowMs(), program.duration)
  }

  // 現在番組の終了時刻を過ぎたら次の番組を取りにいく（要件定義 3.5）
  let lastAdvanceAt = 0
  createEffect(() => {
    const response = now()
    if (!response) return

    // 毎秒この effect を動かすために放送時刻を必ず読む
    const nowMs = broadcastNowMs()
    const sinceLastAdvance = Date.now() - lastAdvanceAt

    if (response.now) {
      if (nowMs < Date.parse(response.now.endAt)) return
      if (sinceLastAdvance < ADVANCE_COOLDOWN_MS) return
    } else if (sinceLastAdvance < OFF_AIR_RETRY_MS) {
      // 放送休止中。番組表が入るまで一定間隔で確認する
      return
    }

    lastAdvanceAt = Date.now()
    void refetch()
  })

  onMount(() => {
    const timer = setInterval(() => setTickMs(Date.now()), TICK_MS)

    // スリープやタブ復帰のあとは時計がずれているので取り直す
    const resync = () => {
      if (document.visibilityState === 'visible') void refetch()
    }
    document.addEventListener('visibilitychange', resync)
    window.addEventListener('online', resync)

    onCleanup(() => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', resync)
      window.removeEventListener('online', resync)
    })
  })

  /** 再生できなかった動画を記録し、次の放送可能な番組へ送る（要件定義 13 章） */
  const markUnavailable = (videoId: string): void => {
    setUnavailable((current) =>
      current.includes(videoId) ? current : [...current, videoId].slice(-MAX_UNAVAILABLE),
    )
  }

  return {
    now,
    refresh: () => void refetch(),
    markUnavailable,
    unavailable,
    broadcastNowMs,
    broadcastOffsetSeconds,
    clockOffsetMs,
    onAir: (): boolean => now()?.onAir === true,
    currentProgram: (): NowResponse['now'] => now()?.now ?? null,
  }
}
